import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiStaff } from '../../api/staffClient';
import { useStaffAuth } from '../../stores/staffAuth';
import TopBar from '../../components/TopBar';

type ReclamoItem = {
  id: number;
  nro_expediente: string;
  titulo: string;
  estado: string;
  prioridad: string;
  creado_en: string;
  ciudadano: string;
  categoria: string | null;
  categoria_icono: string | null;
  area: string | null;
};

const ESTADO_COLOR: Record<string, string> = {
  pendiente:  '#f59e0b',
  en_proceso: '#3b82f6',
  resuelto:   '#10b981',
  cerrado:    '#6b7280',
  rechazado:  '#ef4444',
};
const ESTADO_LABEL: Record<string, string> = {
  pendiente:  'Pendiente',
  en_proceso: 'En proceso',
  resuelto:   'Resuelto',
  cerrado:    'Cerrado',
  rechazado:  'Rechazado',
};
const PRIOR_COLOR: Record<string, string> = {
  urgente: '#ef4444',
  alta:    '#f97316',
  media:   '#3b82f6',
  baja:    '#9ca3af',
};

const FILTERS = [
  { key: '',           label: 'Todos'      },
  { key: 'pendiente',  label: 'Pendiente'  },
  { key: 'en_proceso', label: 'En proceso' },
  { key: 'resuelto',   label: 'Resuelto'   },
];

function fmtFecha(iso: string) {
  return new Date(iso.replace(' ', 'T')).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function StaffReclamos() {
  const nav             = useNavigate();
  const staff           = useStaffAuth((s) => s.staff);
  const [searchParams]  = useSearchParams();
  const initialEstado   = searchParams.get('estado') ?? '';

  const [estado,  setEstado]  = useState(initialEstado);
  const [items,   setItems]   = useState<ReclamoItem[]>([]);
  const [page,    setPage]    = useState(1);
  const [pages,   setPages]   = useState(1);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPage(1);
    setItems([]);
  }, [estado]);

  useEffect(() => {
    setLoading(true);
    const qs = estado ? `&estado=${estado}` : '';
    apiStaff<{ items: ReclamoItem[]; total: number; page: number; pages: number }>(
      `/staff?action=reclamos&page=${page}${qs}`
    )
      .then((d) => {
        setItems((prev) => page === 1 ? d.items : [...prev, ...d.items]);
        setPages(d.pages);
        setTotal(d.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [estado, page]);

  return (
    <>
      <TopBar title="Reclamos" back />

      <main className="max-w-md mx-auto pb-8">

        {/* Filtros */}
        <div className="flex gap-2 px-4 py-3 overflow-x-auto sticky top-[57px] bg-gray-100 z-10"
          style={{ borderBottom: '1px solid #e5e7eb' }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setEstado(f.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                estado === f.key
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-500 border border-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Total */}
        {!loading && total > 0 && (
          <p className="text-xs text-gray-400 px-4 pt-2 pb-1">
            {total} {total === 1 ? 'reclamo' : 'reclamos'}
          </p>
        )}

        {/* Lista */}
        {!loading && items.length === 0 ? (
          <div className="text-center py-16 px-4">
            <span className="text-4xl">📋</span>
            <p className="mt-3 text-gray-400 text-sm">
              {estado
                ? `No hay reclamos en estado "${ESTADO_LABEL[estado] ?? estado}"`
                : staff?.area_nombre
                  ? `No hay reclamos para el área "${staff.area_nombre}"`
                  : 'No hay reclamos'}
            </p>
            {!estado && staff?.area_nombre && (
              <p className="mt-1 text-xs" style={{ color: '#9ca3af' }}>
                Solo se muestran reclamos asignados a tu área.
              </p>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 bg-white border-y border-gray-100">
            {items.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => nav(`/staff/reclamos/${r.id}`)}
                  className="w-full text-left px-4 py-3.5 flex items-start gap-3 active:bg-gray-50 transition-colors"
                >
                  <span className="text-xl leading-none mt-0.5 flex-shrink-0">
                    {r.categoria_icono || '📋'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span
                        className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                        style={{
                          backgroundColor: (ESTADO_COLOR[r.estado] ?? '#6b7280') + '20',
                          color: ESTADO_COLOR[r.estado] ?? '#6b7280',
                        }}
                      >
                        {ESTADO_LABEL[r.estado] ?? r.estado}
                      </span>
                      {r.prioridad && r.prioridad !== 'baja' && (
                        <span
                          className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                          style={{
                            backgroundColor: (PRIOR_COLOR[r.prioridad] ?? '#9ca3af') + '20',
                            color: PRIOR_COLOR[r.prioridad] ?? '#9ca3af',
                          }}
                        >
                          {r.prioridad}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-800 leading-snug">{r.titulo}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {r.ciudadano}
                      {r.area ? ` · ${r.area}` : ''}
                    </p>
                    <p className="text-xs text-gray-300 mt-0.5">{fmtFecha(r.creado_en)}</p>
                  </div>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" width="16" height="16"
                    className="text-gray-300 flex-shrink-0 mt-1">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Cargar más */}
        {page < pages && (
          <div className="px-4 pt-4">
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={loading}
              className="w-full border border-app rounded-xl py-3 text-sm font-semibold text-gray-600 disabled:opacity-50"
            >
              {loading ? 'Cargando…' : 'Ver más'}
            </button>
          </div>
        )}

        {loading && items.length === 0 && (
          <p className="text-center text-gray-400 text-sm py-10">Cargando…</p>
        )}
      </main>
    </>
  );
}
